import React, { useState, useEffect, useCallback } from "react";
import {
  IconUsers,
  IconSend,
  IconDeviceFloppy,
  IconUser,
  IconPlus,
  IconLoader2,
  IconAlertCircle,
  IconFileText,
  IconTarget,
  IconChevronDown,
  IconCurrencyDollar,
  IconPaperclip,
  IconEye,
  IconX,
  IconChecklist,
  IconBuildingCommunity,
  IconDownload,
  IconTrash,
} from "@tabler/icons-react";
import styled from "styled-components";
import fetchApi from "../components/Lib/api";
import SquadFormModal from "./SquadFormModal";
import Mixpanel from "../components/Lib/mixpanel";

const StartupWrapper = styled.div`
  grid-column: 1 / span 6;
  display: flex;
`;

const StartupImage = styled.img`
  width: 110px;
  height: 110px;
  border-radius: 6px;
  object-fit: cover;
  margin-right: 16px;
  cursor: pointer;
  border: 1px solid var(--color-container-card-default);
`;

const StartupContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  margin-left: 8px;
  align-items: flex-start;
`;

const StartupTitle = styled.h2`
  font-size: var(--font-size-h3);
  font-weight: var(--font-weight-body-2);
  color: #111827; /* ensure visible on light background */
  line-height: 26px;
`;

const StartupDescription = styled.span`
  text-align: left;
  margin-right: 8px;
  color: var(--color-background-main);
`;

const UserDisplay = ({ user }) => (
  <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
    {user.avatar_url ? (
      <img
        src={user.avatar_url}
        alt={user.first_name}
        className="w-10 h-10 rounded-full object-cover"
      />
    ) : (
      <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center text-blue-700 font-bold text-lg">
        {user.first_name ? user.first_name[0] : "U"}
      </div>
    )}
    <div>
      <p className="font-semibold text-blue-800">
        {user.first_name} {user.last_name}
      </p>
      <p className="text-sm text-blue-700">Individual Submission</p>
    </div>
  </div>
);

const SquadDisplay = ({ squad }) => (
  <div className="flex items-center gap-3 p-4 bg-purple-50 border border-purple-200 rounded-lg">
    <IconBuildingCommunity size={28} className="text-purple-700" />
    <div>
      <p className="font-semibold text-purple-800">{squad.name}</p>
      <p className="text-sm text-purple-700">{squad.members.length} members</p>
      {squad.members.length > 0 && (
        <div className="flex -space-x-2 overflow-hidden mt-1">
          {squad.members.slice(0, 5).map((member) =>
            member.avatar_url ? (
              <img
                key={member.id}
                className="inline-block h-6 w-6 rounded-full ring-2 ring-white"
                src={member.avatar_url}
                alt={member.first_name}
              />
            ) : (
              <span
                key={member.id}
                className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-purple-300 text-xs font-medium text-purple-800 ring-2 ring-white"
              >
                {member.first_name ? member.first_name[0] : "U"}
              </span>
            )
          )}
          {squad.members.length > 5 && (
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-purple-300 text-xs font-medium text-purple-800 ring-2 ring-white">
              +{squad.members.length - 5}
            </span>
          )}
        </div>
      )}
    </div>
  </div>
);

const ProposalSubmission = ({
  isProjectOwnerView = false,
  initialSquad = null,
  initialUser = null,
  initialProposalText = "",
  initialBudget = "",
  initialAttachedFiles = [],
  project = {},
  initialApplicationSlug = null,
  applicationStatus = "draft",
  rejectionReason = "",
  name,
}) => {
  const [proposalText, setProposalText] = useState(initialProposalText);
  const [budget, setBudget] = useState(initialBudget);
  const [attachedFiles, setAttachedFiles] = useState(initialAttachedFiles);

  const [joinType, setJoinType] = useState(
    initialSquad ? "squad" : initialUser ? "solo" : "solo"
  );
  const [selectedSquad, setSelectedSquad] = useState(initialSquad?.id || "");
  const [userSquads, setUserSquads] = useState([]);
  const [isLoadingSquads, setIsLoadingSquads] = useState(false);
  const [squadError, setSquadError] = useState(null);

  const [showSquadModal, setShowSquadModal] = useState(false);
  const [modalInitialSquadData, setModalInitialSquadData] = useState(null);
  const [isSavingSquad, setIsSavingSquad] = useState(false);
  const [saveSquadError, setSaveSquadError] = useState(null);

  const [showRejectFeedbackModal, setShowRejectFeedbackModal] = useState(false);
  const [rejectionReasonInput, setRejectionReasonInput] = useState("");
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectError, setRejectError] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});

  const projectBudgetEnabled =
    typeof project?.has_budget === "boolean"
      ? project.has_budget
      : project?.budget !== null &&
        project?.budget !== "" &&
        !Number.isNaN(Number(project?.budget)) &&
        Number(project?.budget) > 0;

  const normalizedBudgetValue =
    budget !== null && budget !== undefined && budget !== ""
      ? budget
      : initialBudget;

  const numericBudgetValue = Number(normalizedBudgetValue);

  const applicantBudgetProvided =
    !Number.isNaN(numericBudgetValue) && numericBudgetValue > 0;

  const shouldShowBudgetSection = projectBudgetEnabled || applicantBudgetProvided;

  const formattedBudgetValue = applicantBudgetProvided
    ? `$${numericBudgetValue.toLocaleString()}`
    : "N/A";

  const isRejected = applicationStatus === "rejected";
  const isSubmitted = applicationStatus === "submitted";
  const isLocked = isRejected || applicationStatus === "accepted";
  const isAccepted = applicationStatus === "accepted";

  useEffect(() => {
    if (initialSquad) {
      setUserSquads((prev) => {
        if (!prev.some((s) => s.id === initialSquad.id)) {
          return [...prev, initialSquad];
        }
        return prev;
      });
    }
  }, [initialSquad]);

  const fetchUserSquads = useCallback(async () => {
    if (isRejected) return;

    setIsLoadingSquads(true);
    setSquadError(null);
    try {
      const response = await fetchApi("/squads", "GET");
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to fetch squads: ${response.statusText}`
        );
      }
      const data = await response.json();
      const fetchedOwnedSquads = Array.isArray(data.owned_squads)
        ? data.owned_squads
        : [];

      let combinedSquads = [...fetchedOwnedSquads];
      if (
        initialSquad &&
        !fetchedOwnedSquads.some((s) => s.id === initialSquad.id)
      ) {
        combinedSquads = [...fetchedOwnedSquads, initialSquad];
      }

      setUserSquads(combinedSquads);

      if (initialSquad && initialSquad.id === selectedSquad) {
      } else if (
        selectedSquad &&
        !combinedSquads.some((s) => s.id === selectedSquad)
      ) {
        setSelectedSquad("");
      } else if (
        !selectedSquad &&
        combinedSquads.length > 0 &&
        joinType === "squad" &&
        !initialSquad
      ) {
        setSelectedSquad(combinedSquads[0].id);
      }
    } catch (err) {
      console.error("Error fetching squads:", err);
      setSquadError(err.message || "Failed to load squads.");
      setUserSquads([]);
      setSelectedSquad("");
    } finally {
      setIsLoadingSquads(false);
    }
  }, [selectedSquad, joinType, initialSquad, isRejected]);

  useEffect(() => {
    if (joinType === "squad" && !isProjectOwnerView && !isRejected) {
      fetchUserSquads();
    }
  }, [joinType, fetchUserSquads, isProjectOwnerView, isRejected]);

  const handleOpenCreateSquadModal = useCallback(() => {
    if (isRejected) return;
    setModalInitialSquadData(null);
    setSaveSquadError(null);
    setShowSquadModal(true);
  }, [isRejected]);

  const handleCloseSquadModal = useCallback(() => {
    setShowSquadModal(false);
    setModalInitialSquadData(null);
    setSaveSquadError(null);
  }, []);

  const handleSaveSquad = useCallback(
    async (squadDetails, squadId = null) => {
      if (isRejected) return;

      setIsSavingSquad(true);
      setSaveSquadError(null);
      try {
        let response;
        if (squadId) {
          throw new Error(
            "Editing squads is not supported directly from this proposal form."
          );
        } else {
          response = await fetchApi("/squads", "POST", squadDetails);
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Failed to create squad.`);
        }

        const newSquadResponseData = await response.json();

        handleCloseSquadModal();
        await fetchUserSquads();

        if (newSquadResponseData && newSquadResponseData.id) {
          setSelectedSquad(newSquadResponseData.id);
        }
      } catch (err) {
        console.error("Error saving squad:", err);
        setSaveSquadError(
          err.message || "An unexpected error occurred during squad creation."
        );
      } finally {
        setIsSavingSquad(false);
      }
    },
    [handleCloseSquadModal, fetchUserSquads, isRejected]
  );


  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);

    for (const file of files) {
      const fileId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      Mixpanel.custom('Proposal File Upload Started', {
        file_name: file.name,
        file_size_kb: Math.round(file.size / 1024),
        file_type: file.type,
        project_slug: project.slug,
        application_slug: initialApplicationSlug
      });

      // Add to uploading files with initial progress
      setUploadingFiles((prev) => [
        ...prev,
        {
          id: fileId,
          file,
          name: file.name,
          size: file.size,
          status: "preparing", // preparing, uploading, confirming, completed, error
        },
      ]);

      setUploadProgress((prev) => ({
        ...prev,
        [fileId]: 0,
      }));

      try {
        await uploadFile(file, fileId);

        // Remove from uploading files on success
        setUploadingFiles((prev) => prev.filter((f) => f.id !== fileId));
        setUploadProgress((prev) => {
          const updated = { ...prev };
          delete updated[fileId];
          return updated;
        });

        Mixpanel.custom('Proposal File Upload Completed', {
          status: 'success',
          file_name: file.name,
          project_slug: project.slug,
          application_slug: initialApplicationSlug
        });
      } catch (error) {
        console.error("Upload failed:", error);
        setUploadingFiles((prev) =>
          prev.map((f) =>
            f.id === fileId
              ? { ...f, status: "error", error: error.message }
              : f
          )
        );

        Mixpanel.error('Proposal File Upload Error', {
          error_message: error.message,
          error_type: 'Upload Error',
          component: 'ProposalSubmission',
          user_action: 'Upload Proposal File',
          file_name: file.name,
          project_slug: project.slug,
          application_slug: initialApplicationSlug
        });
      }
    }

    // Clear the input
    event.target.value = "";
  };

  // Helper function to calculate MD5 checksum
  const calculateSHA256Checksum = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const buffer = event.target.result;
          const hashBuffer = await crypto.subtle.digest("SHA-256", buffer); // Use SHA-256
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          // Convert byte array to Base64 string
          const base64Checksum = btoa(
            String.fromCharCode.apply(null, hashArray)
          );
          resolve(base64Checksum);
        } catch (error) {
          reject(
            new Error(`Failed to calculate SHA-256 checksum: ${error.message}`)
          );
        }
      };
      reader.onerror = (error) =>
        reject(new Error(`FileReader error: ${error.message}`));
      reader.readAsArrayBuffer(file);
    });
  };

  // Upload file function
  const uploadFile = async (file, fileId) => {
    try {
      // Update status
      setUploadingFiles((prev) =>
        prev.map((f) => (f.id === fileId ? { ...f, status: "preparing" } : f))
      );

      // Calculate checksum first
      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.id === fileId ? { ...f, status: "calculating_checksum" } : f
        )
      );

      const checksum = await calculateSHA256Checksum(file);

      // Step 1: Get signed URL
      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.id === fileId ? { ...f, status: "connecting" } : f
        )
      );

      const signedUrlResponse = await fetch(
        `/startup_entities/${project.slug}/applications/${initialApplicationSlug}/generate_signed_url`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-TOKEN": document.querySelector('meta[name="csrf-token"]')
              .content,
          },
          body: JSON.stringify({
            application: {
              filename: file.name,
              content_type: file.type,
              checksum: checksum, // Include checksum in signed URL request if needed
            },
          }),
        }
      );

      if (!signedUrlResponse.ok) {
        throw new Error("Failed to get signed URL");
      }

      const { signed_url, file_key } = await signedUrlResponse.json();

      // Step 2: Upload to GCS with progress tracking
      setUploadingFiles((prev) =>
        prev.map((f) => (f.id === fileId ? { ...f, status: "uploading" } : f))
      );

      await uploadToGCS(file, signed_url, fileId);

      // Step 3: Confirm upload with checksum
      setUploadingFiles((prev) =>
        prev.map((f) => (f.id === fileId ? { ...f, status: "confirming" } : f))
      );

      const confirmResponse = await fetch(
        `/startup_entities/${project.slug}/applications/${initialApplicationSlug}/confirm_file_upload`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-TOKEN": document.querySelector('meta[name="csrf-token"]')
              .content,
          },
          body: JSON.stringify({
            file_key,
            filename: file.name,
            content_type: file.type,
            size_bytes: file.size,
            checksum: checksum,
          }),
        }
      );

      if (!confirmResponse.ok) {
        const errorData = await confirmResponse.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to confirm upload");
      }

      const { file: uploadedFile } = await confirmResponse.json();

      // Add to attached files
      setAttachedFiles((prev) => [...prev, uploadedFile]);
      setUploadingFiles((prev) =>
        prev.map((f) => (f.id === fileId ? { ...f, status: "completed" } : f))
      );
    } catch (error) {
      console.error("Upload failed:", error);
      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.id === fileId ? { ...f, status: "error", error: error.message } : f
        )
      );
      throw error;
    }
  };

  // Upload to GCS with progress
  const uploadToGCS = (file, signedUrl, fileId) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percentCompleted = Math.round(
            (event.loaded * 100) / event.total
          );
          setUploadProgress((prev) => ({
            ...prev,
            [fileId]: percentCompleted,
          }));
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status === 200) {
          setUploadProgress((prev) => ({
            ...prev,
            [fileId]: 100,
          }));
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Upload failed"));
      });

      xhr.open("PUT", signedUrl);
      xhr.setRequestHeader("Content-Type", file.type);
      xhr.send(file);
    });
  };

  // Delete file function
  const handleDeleteFile = async (fileId) => {
    Mixpanel.custom('Proposal File Deletion Started', {
      file_id: fileId,
      project_slug: project.slug,
      application_slug: initialApplicationSlug
    });
    const startTime = performance.now();
    try {
      const response = await fetch(
        `/startup_entities/${project.slug}/applications/${initialApplicationSlug}/files/${fileId}`,
        {
          method: "DELETE",
          headers: {
            "X-CSRF-TOKEN": document.querySelector('meta[name="csrf-token"]')
              .content,
          },
        }
      );

      const responseTime = Math.round(performance.now() - startTime);

      if (!response.ok) {
        throw new Error("Failed to delete file");
      }

      // Remove from attached files
      setAttachedFiles((prev) => prev.filter((file) => file.id !== fileId));
      Mixpanel.content('Proposal File', 'Deleted', {
        deleted_file_id: fileId,
        response_time_ms: responseTime,
        project_slug: project.slug,
        application_slug: initialApplicationSlug
    });
    } catch (error) {
      const responseTime = Math.round(performance.now() - startTime);
      console.error("Delete failed:", error);
      Mixpanel.error('Proposal File Deletion Error', {
        error_message: error.message,
        error_type: 'Delete Error',
        component: 'ProposalSubmission',
        user_action: 'Delete Proposal File',
        file_id: fileId,
        project_slug: project.slug,
        application_slug: initialApplicationSlug,
        response_time_ms: responseTime
      });
    }
  };

  const handleConfirm = async () => {
    if (isRejected) return;

    if (!proposalText.trim()) {
      alert("Please enter your proposal before submitting.");
      return;
    }

    if (joinType === "squad" && !selectedSquad) {
      alert("Please select a squad to join with.");
      return;
    }

    if (
      projectBudgetEnabled &&
      (budget === null ||
        budget === undefined ||
        budget === "" ||
        isNaN(parseFloat(budget)))
    ) {
      alert("Please enter a valid budget amount.");
      return;
    }

    if (!initialApplicationSlug) {
      alert("Error: Application not found for submission.");
      return;
    }

    const formData = new FormData();
    formData.append("application[pitch]", proposalText);
    formData.append("application[status]", "submitted");
    if (projectBudgetEnabled) {
    formData.append("application[budget]", parseFloat(budget));
    } else {
      formData.append("application[budget]", "");
    }

    if (joinType === "squad") {
      formData.append("application[squad_id]", selectedSquad);
    }

    const startTime = performance.now();

    try {
      const response = await fetchApi(
        `/startup_entities/${project.slug}/applications/${initialApplicationSlug}`,
        "PUT",
        formData,
        true
      );

      const responseTime = Math.round(performance.now() - startTime);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to submit proposal: ${response.statusText}`
        );
      }
      const data = await response.json();
      alert("Proposal submitted successfully!");
      console.log("Proposal submitted:", data);
      Mixpanel.form('Proposal Submission', 'Submitted', {
        status: 'success',
        response_time_ms: responseTime,
        project_slug: project.slug,
        application_slug: initialApplicationSlug,
        join_type: joinType,
        squad_id: joinType === 'squad' ? selectedSquad : null,
        budget: projectBudgetEnabled ? parseFloat(budget) : null
      });
      window.location.href = `/projects/${project.slug}`;
    } catch (error) {
      const responseTime = Math.round(performance.now() - startTime);
      console.error("Error submitting proposal:", error);
      Mixpanel.error('Proposal Submission Error', {
        error_message: error.message,
        error_type: 'API Error',
        component: 'ProposalSubmission',
        user_action: 'Submit Proposal',
        project_slug: project.slug,
        application_slug: initialApplicationSlug,
        response_time_ms: responseTime
      });
      alert(`Failed to submit proposal: ${error.message}`);
    }
  };

  const handleSaveForLater = async () => {
    if (isRejected || isSubmitted) return;

    setSaveError(null);

    if (!proposalText.trim()) {
      setSaveError("Please enter some text for your proposal to save.");
      return;
    }

    if (!initialApplicationSlug) {
      setSaveError("Error: Application not found for saving.");
      return;
    }

    const formData = new FormData();
    formData.append("application[pitch]", proposalText);
    formData.append("application[status]", "draft");
    if (projectBudgetEnabled) {
      formData.append(
        "application[budget]",
        budget ? parseFloat(budget) : ""
      );
    } else {
      formData.append("application[budget]", "");
    }

    if (joinType === "squad" && selectedSquad) {
      formData.append("application[squad_id]", selectedSquad);
    }

    const startTime = performance.now();

    try {
      const response = await fetchApi(
        `/startup_entities/${project.slug}/applications/${initialApplicationSlug}`,
        "PUT",
        formData,
        true
      );

      const responseTime = Math.round(performance.now() - startTime);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to save proposal: ${response.statusText}`
        );
      }
      const data = await response.json();
      Mixpanel.form('Proposal', 'Saved Draft', {
        status: 'success',
        response_time_ms: responseTime,
        project_slug: project.slug,
        application_slug: initialApplicationSlug,
        join_type: joinType,
        squad_id: joinType === 'squad' ? selectedSquad : null,
        budget:
          projectBudgetEnabled && budget ? parseFloat(budget) : null
      });

      window.location.href = `/projects/${project.slug}`;
    } catch (error) {
      const responseTime = Math.round(performance.now() - startTime);
      console.error("Error saving proposal:", error);
      setSaveError(error.message || "Failed to save proposal. Please try again.");
      Mixpanel.error('Proposal Save Error', {
        error_message: error.message,
        error_type: 'API Error',
        component: 'ProposalSubmission',
        user_action: 'Save Proposal Draft',
        project_slug: project.slug,
        application_slug: initialApplicationSlug,
        response_time_ms: responseTime
      });
    }
  };

  const handleSaveChanges = async () => {
    if (isRejected || !isSubmitted) return;

    setSaveError(null);

    if (!proposalText.trim()) {
      setSaveError("Please enter some text for your proposal to save.");
      return;
    }

    if (!initialApplicationSlug) {
      setSaveError("Error: Application not found for saving.");
      return;
    }

    const formData = new FormData();
    formData.append("application[pitch]", proposalText);
    formData.append("application[status]", "submitted");
    if (projectBudgetEnabled) {
      formData.append(
        "application[budget]",
        budget ? parseFloat(budget) : ""
      );
    } else {
      formData.append("application[budget]", "");
    }

    if (joinType === "squad" && selectedSquad) {
      formData.append("application[squad_id]", selectedSquad);
    }

    const startTime = performance.now();

    try {
      const response = await fetchApi(
        `/startup_entities/${project.slug}/applications/${initialApplicationSlug}`,
        "PUT",
        formData,
        true
      );

      const responseTime = Math.round(performance.now() - startTime);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to save changes: ${response.statusText}`
        );
      }
      const data = await response.json();
      Mixpanel.form('Proposal', 'Saved Changes', {
        status: 'success',
        response_time_ms: responseTime,
        project_slug: project.slug,
        application_slug: initialApplicationSlug,
        join_type: joinType,
        squad_id: joinType === 'squad' ? selectedSquad : null,
        budget:
          projectBudgetEnabled && budget ? parseFloat(budget) : null
      });

      alert("Changes saved successfully!");
      window.location.href = `/projects/${project.slug}`;
    } catch (error) {
      const responseTime = Math.round(performance.now() - startTime);
      console.error("Error saving changes:", error);
      setSaveError(error.message || "Failed to save changes. Please try again.");
      Mixpanel.error('Proposal Save Changes Error', {
        error_message: error.message,
        error_type: 'API Error',
        component: 'ProposalSubmission',
        user_action: 'Save Proposal Changes',
        project_slug: project.slug,
        application_slug: initialApplicationSlug,
        response_time_ms: responseTime
      });
    }
  };

  const handleAcceptProposal = async () => {
    if (isRejected) return;

    const applicationSlug = initialApplicationSlug;

    if (!applicationSlug) {
      alert("Error: Application ID/slug not found for acceptance.");
      return;
    }

    setIsRejecting(false);
    setRejectError(null);

    const startTime = performance.now();

    try {
      const response = await fetchApi(
        `/startup_entities/${project.slug}/applications/${applicationSlug}/accept_application`,
        "POST"
      );

      const responseTime = Math.round(performance.now() - startTime);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to accept proposal: ${response.statusText}`
        );
      }
      const data = await response.json();
      const acceptedProject = data.accepted_project || data.application?.accepted_startup_entity;
      const acceptedSlug = acceptedProject?.slug;

      alert(
        acceptedProject?.name
          ? `Proposal accepted! We've created a workspace for this team: ${acceptedProject.name}`
          : "Proposal has been accepted!"
      );
      console.log("Proposal accepted:", data);
      Mixpanel.settings('Project Application', 'Accepted', {
        project_slug: project.slug,
        application_slug: applicationSlug,
        response_time_ms: responseTime,
        accepted_project_slug: acceptedSlug,
        accepted_project_name: acceptedProject?.name
    });

      if (acceptedSlug) {
        window.location.href = `/projects/${acceptedSlug}`;
      } else {
      window.location.href = `/projects/${project.slug}`;
      }
    } catch (error) {
      const responseTime = Math.round(performance.now() - startTime);
      console.error("Error accepting proposal:", error);
      Mixpanel.error('Proposal Acceptance Error', {
        error_message: error.message,
        error_type: 'API Error',
        component: 'ProposalSubmission',
        user_action: 'Accept Proposal',
        project_slug: project.slug,
        application_slug: applicationSlug,
        response_time_ms: responseTime
      });
      alert(`Failed to accept proposal: ${error.message}`);
    }
  };

  const handleRejectProposalRequest = () => {
    if (isRejected) return;
    setShowRejectFeedbackModal(true);
  };

  const handleRejectProposal = async () => {
    if (isRejected) return;

    if (!rejectionReasonInput.trim()) {
      setRejectError("Please provide a reason for rejection.");
      return;
    }

    const applicationSlug = initialApplicationSlug;

    if (!applicationSlug) {
      setRejectError("Error: Application ID/slug not found for rejection.");
      return;
    }

    setIsRejecting(true);
    setRejectError(null);

    const startTime = performance.now();

    try {
      const response = await fetchApi(
        `/startup_entities/${project.slug}/applications/${applicationSlug}`,
        "PUT",
        {
          application: {
            status: "rejected",
            rejection_reason: rejectionReasonInput,
          },
        }
      );

      const responseTime = Math.round(performance.now() - startTime);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to reject proposal: ${response.statusText}`
        );
      }
      const data = await response.json();
      alert("Proposal has been rejected!");
      console.log("Proposal rejected:", data);
      Mixpanel.settings('Project Application', 'Rejected', {
        project_slug: project.slug,
        application_slug: applicationSlug,
        rejection_reason: rejectionReasonInput,
        response_time_ms: responseTime
    });
      setShowRejectFeedbackModal(false);
      setRejectionReasonInput("");
      window.location.href = `/projects/${project.slug}`;
    } catch (error) {
      const responseTime = Math.round(performance.now() - startTime);
      console.error("Error rejecting proposal:", error);
      Mixpanel.error('Proposal Rejection Error', {
        error_message: error.message,
        error_type: 'API Error',
        component: 'ProposalSubmission',
        user_action: 'Reject Proposal',
        project_slug: project.slug,
        application_slug: applicationSlug,
        response_time_ms: responseTime
      });
      setRejectError(`Failed to reject proposal: ${error.message}`);
    } finally {
      setIsRejecting(false);
    }
  };

  const displaySquadOrUser = initialSquad
    ? initialSquad
    : initialUser
    ? initialUser
    : null;
  const displayJoinType = initialSquad
    ? "squad"
    : initialUser
    ? "solo"
    : joinType;

  return (
    <div className="w-full">
      <div className="bg-white rounded-xl shadow-xs border border-gray-200 overflow-hidden">
        {isRejected && rejectionReason && (
          <div className="bg-red-50 border-l-4 border-red-500 p-6 m-6 rounded-lg">
            <div className="flex items-start">
              <IconAlertCircle
                size={24}
                className="text-red-500 flex-shrink-0 mt-0.5"
              />
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-red-800">
                  Application Rejected
                </h3>
                <p className="text-red-700 mt-2 leading-relaxed">
                  {rejectionReason}
                </p>
              </div>
            </div>
          </div>
        )}

        {isProjectOwnerView && (
          <div className="bg-gray-50 px-8 py-5 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <IconEye size={24} className="text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">
                Review Proposal
              </h2>
            </div>
            <span
              className={`px-4 py-2 rounded-full text-sm font-semibold ${
                isRejected
                  ? "bg-red-100 text-red-800"
                  : isAccepted
                  ? "bg-green-100 text-green-800"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {isRejected
                ? "Rejected"
                : isAccepted
                ? "Accepted"
                : isSubmitted
                ? "Submitted"
                : "Pending Review"}
            </span>
          </div>
        )}

        <div className="p-8 space-y-8">
          {(applicationStatus === "accepted" || isRejected) && (
            <div className={`flex items-center justify-between px-4 py-3 rounded-lg border ${
              applicationStatus === "accepted"
                ? "bg-green-50 border-green-200"
                : "bg-red-50 border-red-200"
            }`}>
              <div className="flex items-center gap-3">
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  applicationStatus === "accepted"
                    ? "bg-green-200 text-green-900"
                    : "bg-red-200 text-red-900"
                }`}>
                  {applicationStatus === "accepted" ? "Accepted" : "Rejected"}
                </span>
                <p className={`${applicationStatus === "accepted" ? "text-green-900" : "text-red-900"} text-sm`}>
                  {applicationStatus === "accepted"
                    ? "This application has been accepted. Further edits are disabled."
                    : "This application has been rejected. Further edits are disabled."}
                </p>
              </div>
            </div>
          )}
          <StartupWrapper>
            <StartupImage
              src={
                project.image?.url ||
                "https://flowbite.com/docs/images/examples/image-3@2x.jpg"
              }
              alt="Startup"
            />

            <StartupContent>
              <StartupTitle>{name || project.name || 'Untitled Project'}</StartupTitle>
              <StartupDescription>{project.description}</StartupDescription>
            </StartupContent>
          </StartupWrapper>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div
              className={`space-y-4 ${
                isProjectOwnerView ? "lg:col-span-2" : ""
              }`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-gray-100 rounded-md flex items-center justify-center">
                  <IconUsers size={16} className="text-gray-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">
                  Join Method
                </h3>
              </div>

              {isProjectOwnerView ? (
                displayJoinType === "solo" && initialUser ? (
                  <UserDisplay user={initialUser} />
                ) : displayJoinType === "squad" && initialSquad ? (
                  <SquadDisplay squad={initialSquad} />
                ) : (
                  <div className="px-6 py-4 rounded-lg border border-gray-300 text-gray-700 bg-gray-50 flex items-center justify-center">
                    <span className="font-medium text-gray-500">
                      No join method specified.
                    </span>
                  </div>
                )
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                    <label
                      className={`group cursor-pointer flex items-center justify-center p-4 rounded-lg border transition-all duration-200 ${
                        isRejected || isSubmitted
                          ? "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
                          : joinType === "solo"
                          ? "bg-blue-600 border-blue-600 text-white shadow-md"
                          : "bg-white border-gray-300 text-gray-700 hover:border-blue-400 hover:shadow-sm"
                      }`}
                    >
                      <input
                        type="radio"
                        name="joinType"
                        value="solo"
                        checked={joinType === "solo"}
                        onChange={() => !isRejected && !isSubmitted && setJoinType("solo")}
                        className="hidden"
                        disabled={isRejected || isSubmitted}
                      />
                      <IconUser size={20} className="mr-3" />
                      <span className="font-medium">Solo</span>
                    </label>
                    <label
                      className={`group cursor-pointer flex items-center justify-center p-4 rounded-lg border transition-all duration-200 ${
                        isRejected || isSubmitted
                          ? "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
                          : joinType === "squad"
                          ? "bg-blue-600 border-blue-600 text-white shadow-md"
                          : "bg-white border-gray-300 text-gray-700 hover:border-blue-400 hover:shadow-sm"
                      }`}
                    >
                      <input
                        type="radio"
                        name="joinType"
                        value="squad"
                        checked={joinType === "squad"}
                        onChange={() => !isRejected && !isSubmitted && setJoinType("squad")}
                        className="hidden"
                        disabled={isRejected || isSubmitted}
                      />
                      <IconUsers size={20} className="mr-3" />
                      <span className="font-medium">Squad</span>
                    </label>
                  </div>

                  {joinType === "squad" && (
                    <div
                      className="space-y-4 p-6 rounded-lg border bg-gray-50 border-gray-200"
                    >
                      <label
                        htmlFor="squad-select"
                        className="block text-base font-semibold text-gray-800 mb-3"
                      >
                        Select Your Squad
                      </label>

                      {isLoadingSquads && !isRejected && (
                        <div className="flex items-center justify-center py-8 text-gray-600">
                          <IconLoader2
                            size={24}
                            className="animate-spin mr-3"
                          />
                          <span className="font-medium">
                            Loading your squads...
                          </span>
                        </div>
                      )}

                      {squadError && !isRejected && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 text-red-700">
                          <IconAlertCircle
                            size={20}
                            className="flex-shrink-0 mt-0.5"
                          />
                          <div>
                            <p className="font-semibold">Error</p>
                            <p className="text-sm">{squadError}</p>
                          </div>
                        </div>
                      )}

                      {(!isLoadingSquads && !squadError) || isRejected ? (
                        <div className="space-y-4">
                          {userSquads.length > 0 ||
                          (isRejected && initialSquad) ? (
                            <div className="relative">
                              <select
                                id="squad-select"
                                value={selectedSquad}
                                onChange={(e) =>
                                  !isRejected &&
                                  setSelectedSquad(Number(e.target.value))
                                }
                                className={`w-full appearance-none px-4 py-3 pr-10 border rounded-lg transition-colors text-gray-800 bg-white shadow-sm font-medium ${
                                  isRejected
                                    ? "border-gray-300 cursor-not-allowed bg-gray-100 text-gray-500"
                                    : "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                }`}
                                disabled={isRejected}
                              >
                                <option value="">Choose your squad...</option>
                                {userSquads.map((squad) => (
                                  <option key={squad.id} value={squad.id}>
                                    {squad.name} ({squad.members?.length || 0}{" "}
                                    members)
                                  </option>
                                ))}
                              </select>
                              <IconChevronDown
                                size={20}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                              />
                            </div>
                          ) : (
                            <div className="text-center py-6 text-gray-600 bg-white rounded-lg border border-gray-200">
                              <IconUsers
                                size={32}
                                className="mx-auto mb-3 text-gray-400"
                              />
                              <p className="font-medium">No squads found</p>
                              <p className="text-sm">
                                Create your first squad to get started
                              </p>
                            </div>
                          )}

                          {!isRejected && (
                            <>
                              <div className="flex items-center gap-4">
                                <div className="flex-1 h-px bg-gray-200"></div>
                                <span className="text-xs font-medium text-gray-600 bg-gray-50 px-3 py-1 rounded-full">
                                  OR
                                </span>
                                <div className="flex-1 h-px bg-gray-200"></div>
                              </div>

                              <button
                                type="button"
                                onClick={handleOpenCreateSquadModal}
                                className="w-full flex items-center justify-center px-6 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors duration-200 font-medium group"
                                disabled={isRejected}
                              >
                                <IconPlus
                                  size={20}
                                  className="mr-3 group-hover:scale-105 transition-transform"
                                />
                                Create New Squad
                              </button>
                            </>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <hr className="border-gray-200" />

          {shouldShowBudgetSection && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-gray-100 rounded-md flex items-center justify-center">
                <IconCurrencyDollar size={16} className="text-gray-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800">
                Proposed Budget
              </h3>
            </div>

              {isProjectOwnerView ||
              isRejected ||
              (!projectBudgetEnabled && applicantBudgetProvided) ? (
              <div className="px-6 py-4 rounded-lg border border-gray-300 text-gray-700 bg-gray-50 flex items-center">
                  <span className="font-medium">{formattedBudgetValue}</span>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="number"
                  id="budget"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  placeholder="e.g., 5000"
                  className="w-full px-6 py-4 pl-12 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 bg-white shadow-sm font-medium placeholder-gray-400"
                  min="0"
                  step="any"
                  disabled={isRejected}
                />
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-bold">
                  $
                </span>
                <div className="absolute bottom-4 right-4 bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-gray-500 border border-gray-200">
                  USD
                </div>
              </div>
            )}
          </div>
          )}

          <hr className="border-gray-200" />

          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-gray-100 rounded-md flex items-center justify-center">
                <IconPaperclip size={16} className="text-gray-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800">
                Attached Files
              </h3>
            </div>

            {/* Existing Files */}
            {(initialAttachedFiles.length > 0 || attachedFiles.length > 0) && (
              <div className="space-y-2">
                {/* Newly attached files */}
                {attachedFiles.map((file, index) => (
                  <div
                    key={`attached-${file.id}`}
                    className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200"
                  >
                    <div className="flex items-center gap-3">
                      <IconFileText
                        size={18}
                        className="text-green-600 flex-shrink-0"
                      />
                      <div>
                        <span className="font-medium text-gray-800">
                          {file.filename}
                        </span>
                        {file.byte_size && (
                          <span className="text-sm text-gray-500 ml-2">
                            ({(file.byte_size / 1024 / 1024).toFixed(1)} MB)
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {file.url && (
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="Download file"
                        >
                          <IconDownload size={16} />
                        </a>
                      )}
                      {!isProjectOwnerView && (
                        <button
                          onClick={() => {
                            if (!isLocked) handleDeleteFile(file.id);
                          }}
                          className={`p-1 ${
                            isLocked
                              ? "text-gray-400 cursor-not-allowed"
                              : "text-red-600 hover:text-red-800"
                          }`}
                          title={isLocked ? "Cannot delete files after acceptance/rejection" : "Delete file"}
                          disabled={isLocked}
                        >
                          <IconTrash size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Uploading Files Progress */}
            {uploadingFiles.length > 0 && (
              <div className="space-y-2">
                {uploadingFiles.map((uploadFile) => (
                  <div
                    key={uploadFile.id}
                    className="p-3 bg-blue-50 rounded-lg border border-blue-200"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <IconFileText
                          size={18}
                          className="text-blue-500 flex-shrink-0"
                        />
                        <span className="font-medium text-gray-800">
                          {uploadFile.name}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {uploadFile.status === "error" ? (
                          <span className="text-red-600">Error</span>
                        ) : uploadFile.status === "completed" ? (
                          <span className="text-green-600">✓ Completed</span>
                        ) : (
                          <span className="capitalize">
                            {uploadFile.status}...
                          </span>
                        )}
                      </div>
                    </div>

                    {uploadFile.status === "uploading" && (
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${uploadProgress[uploadFile.id] || 0}%`,
                          }}
                        ></div>
                      </div>
                    )}

                    {uploadFile.status === "error" && (
                      <div className="text-sm text-red-600 mt-1">
                        {uploadFile.error || "Upload failed"}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Upload Area */}
            {!isProjectOwnerView && (
              <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200 ${
                isLocked
                  ? "border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed"
                  : "border-gray-300 bg-gray-50 hover:border-blue-400 cursor-pointer"
              }`}>
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  disabled={
                    isLocked ||
                    uploadingFiles.some((f) => f.status === "uploading")
                  }
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
                />
                <label
                  htmlFor="file-upload"
                  className={`block ${isLocked ? "cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <IconPaperclip
                    size={32}
                    className="mx-auto mb-3 text-gray-400"
                  />
                  <p className="font-semibold text-gray-700">
                    {isLocked ? (
                      "File uploads are disabled for this application."
                    ) : (
                      <>
                    Drag & drop files here, or{" "}
                        <span className="text-blue-600 hover:underline">browse</span>
                      </>
                    )}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Max file size: 10MB per file • Allowed: PDF, DOC, XLS,
                    Images, TXT
                  </p>
                </label>

                {uploadingFiles.some((f) => f.status === "uploading") && (
                  <div className="mt-4 text-sm text-blue-600">
                    Uploading files, please wait...
                  </div>
                )}
              </div>
            )}

            {/* No files message */}
            {isProjectOwnerView &&
              initialAttachedFiles.length === 0 &&
              attachedFiles.length === 0 && (
                <div className="px-6 py-4 rounded-lg border border-gray-300 text-gray-700 bg-gray-50 flex items-center justify-center">
                  <span className="font-medium text-gray-500">
                    No files attached.
                  </span>
                </div>
              )}
          </div>

          <hr className="border-gray-200" />

          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-gray-100 rounded-md flex items-center justify-center">
                <IconFileText size={16} className="text-gray-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800">
                Your Proposal
              </h3>
            </div>

            {isProjectOwnerView || isRejected ? (
              <div className="relative p-6 border border-gray-300 rounded-lg bg-gray-50 text-gray-800 leading-relaxed max-h-64 overflow-y-auto custom-scrollbar">
                {initialProposalText || (
                  <span className="text-gray-500">
                    No proposal text provided.
                  </span>
                )}
                <div className="absolute bottom-4 right-4 bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-gray-500 border border-gray-200">
                  {initialProposalText.length} chars
                </div>
              </div>
            ) : (
              <div className="relative">
                <textarea
                  id="proposal-text"
                  value={proposalText}
                  onChange={(e) => setProposalText(e.target.value)}
                  placeholder="Describe your background, skills, and why you'd like to join this project. Include relevant experience and what you can contribute..."
                  className="w-full h-48 px-6 py-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition-all duration-200 text-gray-900 bg-white shadow-sm font-medium placeholder-gray-400"
                  disabled={isLocked}
                />
                <div className="absolute bottom-4 right-4 bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-gray-500 border border-gray-200">
                  {proposalText.length} chars
                </div>
              </div>
            )}

            {!isProjectOwnerView && (
              <div className="flex justify-between items-center text-sm">
                <div className="flex items-center gap-2 text-gray-500">
                  <IconTarget size={16} />
                  <span>Minimum 50 characters recommended</span>
                </div>
                <div
                  className={`font-medium ${
                    proposalText.length >= 50
                      ? "text-green-600"
                      : "text-gray-400"
                  }`}
                >
                  {proposalText.length >= 50
                    ? "✓ Good length"
                    : "Keep writing..."}
                </div>
              </div>
            )}
          </div>

          <hr className="border-gray-200" />

          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-8 border-t border-gray-200">
            {!isRejected && (
              <>
                {isProjectOwnerView && applicationStatus !== "accepted" ? (
                  <>
                    <button
                      onClick={handleRejectProposalRequest}
                      className="group flex items-center justify-center px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 focus:ring-2 focus:ring-red-400 focus:ring-offset-2 transition-all duration-200 font-semibold shadow-sm text-base"
                    >
                      <IconX
                        size={20}
                        className="mr-2 group-hover:scale-105 transition-transform"
                      />
                      Reject Proposal
                    </button>
                    <button
                      onClick={handleAcceptProposal}
                      className="group flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 font-semibold shadow-sm text-base"
                    >
                      <IconChecklist
                        size={20}
                        className="mr-2 group-hover:scale-105 transition-transform"
                      />
                      Accept Proposal
                    </button>
                  </>
                ) : isSubmitted ? (
                  <button
                    onClick={handleSaveChanges}
                    className="group flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 font-semibold shadow-md hover:shadow-lg text-base"
                  >
                    <IconDeviceFloppy
                      size={20}
                      className="mr-2 group-hover:scale-105 transition-transform"
                    />
                    Save Changes
                  </button>
                ) : (!isRejected && applicationStatus !== "accepted") ? (
                  <>
                    <button
                      onClick={handleSaveForLater}
                      className="group flex items-center justify-center px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-all duration-200 font-semibold shadow-sm hover:shadow-md text-base"
                    >
                      <IconDeviceFloppy
                        size={20}
                        className="mr-2 group-hover:scale-105 transition-transform"
                      />
                      Save for Later
                    </button>
                    <button
                      onClick={handleConfirm}
                      className="group flex items-center justify-center px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 focus:ring-2 focus:ring-gray-600 focus:ring-offset-2 transition-all duration-200 font-semibold shadow-md hover:shadow-lg text-base"
                    >
                      <IconSend
                        size={20}
                        className="mr-2 group-hover:scale-105 transition-transform"
                      />
                      Submit Proposal
                    </button>
                  </>
                ) : null}
              </>
            )}
          </div>
          {saveError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 text-red-700 mt-4">
              <IconAlertCircle
                size={20}
                className="flex-shrink-0 mt-0.5"
              />
              <div className="flex-1">
                <p className="font-semibold text-sm mb-1">Error</p>
                <p className="text-sm">{saveError}</p>
              </div>
              <button
                onClick={() => setSaveError(null)}
                className="flex-shrink-0 text-red-700 hover:text-red-900"
                aria-label="Dismiss error"
              >
                <IconX size={18} />
              </button>
            </div>
          )}
        </div>
      </div>

      {showRejectFeedbackModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Reject Proposal
            </h3>
            <p className="text-gray-700 mb-6">
              Please provide a reason for rejecting this application:
            </p>
            <textarea
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 resize-none mb-4"
              rows="5"
              placeholder="e.g., 'The budget requested is too high for this project scope,' or 'The proposed solution does not align with our current objectives.'"
              value={rejectionReasonInput}
              onChange={(e) => setRejectionReasonInput(e.target.value)}
            ></textarea>
            {rejectError && (
              <p className="text-red-600 text-sm mb-4">{rejectError}</p>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowRejectFeedbackModal(false);
                  setRejectionReasonInput("");
                  setRejectError(null);
                }}
                className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition duration-200"
                disabled={isRejecting}
              >
                Cancel
              </button>
              <button
                onClick={handleRejectProposal}
                className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-200 flex items-center justify-center"
                disabled={isRejecting}
              >
                {isRejecting ? (
                  <>
                    <IconLoader2 size={20} className="animate-spin mr-2" />
                    Rejecting...
                  </>
                ) : (
                  <>
                    <IconX size={20} className="mr-2" />
                    Confirm Rejection
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {!isProjectOwnerView && (
        <SquadFormModal
          show={showSquadModal}
          onClose={handleCloseSquadModal}
          initialSquadData={modalInitialSquadData}
          onSaveSquad={handleSaveSquad}
          isLoading={isSavingSquad}
          error={saveSquadError}
        />
      )}
    </div>
  );
};

export default ProposalSubmission;
